
import React, { useState } from 'react';

const ContactFormSection: React.FC = () => {
  const [formData, setFormData] = useState({
    inquiryType: '',
    content: '',
    name: '',
    furigana: '',
    email: '',
    phone: '',
    contactMethod: '',
    contactTime: '',
    address: '',
    propertyInfo: '',
    privacyAgreement: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // バリデーション
    if (!formData.inquiryType || !formData.content || !formData.name || !formData.email || !formData.phone || !formData.privacyAgreement) {
      alert('必須項目をすべて入力してください。');
      return;
    }

    if (formData.content.length > 500) {
      alert('お問い合わせ内容は500文字以内で入力してください。');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('inquiryType', formData.inquiryType);
      formDataToSend.append('content', formData.content);
      formDataToSend.append('name', formData.name);
      formDataToSend.append('furigana', formData.furigana);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('contactMethod', formData.contactMethod);
      formDataToSend.append('contactTime', formData.contactTime);
      formDataToSend.append('address', formData.address);
      formDataToSend.append('propertyInfo', formData.propertyInfo);
      formDataToSend.append('privacyAgreement', formData.privacyAgreement ? 'true' : 'false');

      const response = await fetch('https://readdy.ai/api/form/d3gu8sp5ls7rqlnfi300', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          inquiryType: '',
          content: '',
          name: '',
          furigana: '',
          email: '',
          phone: '',
          contactMethod: '',
          contactTime: '',
          address: '',
          propertyInfo: '',
          privacyAgreement: false
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">お問い合わせフォーム</h2>
            <p className="text-lg text-gray-600">
              不動産に関するご相談やご質問がございましたら、お気軽にお問い合わせください。
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
              {/* お問い合わせ種別 */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  お問い合わせ種別 <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {['物件を探している', '物件を売りたい', '賃貸を探している', 'その他'].map((option) => (
                    <label key={option} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="inquiryType"
                        value={option}
                        checked={formData.inquiryType === option}
                        onChange={handleInputChange}
                        className="mr-3 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-gray-700">{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* お問い合わせ内容 */}
              <div className="mb-6">
                <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
                  お問い合わせ内容 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="content"
                  name="content"
                  rows={5}
                  value={formData.content}
                  onChange={handleInputChange}
                  maxLength={500}
                  placeholder="希望エリアは横浜市内、3LDKを希望していますなど、具体的にご記入ください"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <p className="text-sm text-gray-500 mt-1">{formData.content.length}/500文字</p>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {/* お名前 */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="山田太郎"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>

                {/* ふりがな */}
                <div>
                  <label htmlFor="furigana" className="block text-sm font-medium text-gray-700 mb-2">
                    ふりがな
                  </label>
                  <input
                    type="text"
                    id="furigana"
                    name="furigana"
                    value={formData.furigana}
                    onChange={handleInputChange}
                    placeholder="やまだたろう"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {/* メールアドレス */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="example@email.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>

                {/* 電話番号 */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    電話番号 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="03-1234-5678"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                  <p className="text-sm text-gray-500 mt-1">日中つながる番号をご入力ください</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {/* ご希望の連絡方法 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    ご希望の連絡方法
                  </label>
                  <div className="space-y-2">
                    {['電話', 'メール', 'どちらでも可'].map((option) => (
                      <label key={option} className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="contactMethod"
                          value={option}
                          checked={formData.contactMethod === option}
                          onChange={handleInputChange}
                          className="mr-3 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-gray-700">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* ご希望の連絡時間帯 */}
                <div>
                  <label htmlFor="contactTime" className="block text-sm font-medium text-gray-700 mb-2">
                    ご希望の連絡時間帯
                  </label>
                  <select
                    id="contactTime"
                    name="contactTime"
                    value={formData.contactTime}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                  >
                    <option value="">選択してください</option>
                    <option value="午前（9〜12時）">午前（9〜12時）</option>
                    <option value="午後（12〜17時）">午後（12〜17時）</option>
                    <option value="夕方以降">夕方以降</option>
                    <option value="特になし">特になし</option>
                  </select>
                </div>
              </div>

              {/* ご住所 */}
              <div className="mb-6">
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                  ご住所
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="東京都渋谷区神宮前1-1-1"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>

              {/* 物件情報 */}
              <div className="mb-6">
                <label htmlFor="propertyInfo" className="block text-sm font-medium text-gray-700 mb-2">
                  物件情報
                </label>
                <textarea
                  id="propertyInfo"
                  name="propertyInfo"
                  rows={3}
                  value={formData.propertyInfo}
                  onChange={handleInputChange}
                  placeholder="売却相談の場合：物件住所・築年数・間取り・査定希望時期などをご記入ください"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>

              {/* 個人情報の取扱い同意 */}
              <div className="mb-8">
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    name="privacyAgreement"
                    checked={formData.privacyAgreement}
                    onChange={handleInputChange}
                    className="mt-1 mr-3 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    <span className="text-red-500">*</span>
                    <a href="#" className="text-blue-600 hover:underline">プライバシーポリシー</a>に同意します
                  </span>
                </label>
              </div>

              {/* 送信ボタン */}
              <div className="text-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-12 py-4 rounded-lg text-lg font-semibold transition-colors duration-300 whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? '送信中...' : 'この内容で送信する'}
                </button>
              </div>

              {/* 送信状況表示 */}
              {submitStatus === 'success' && (
                <div className="mt-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                  お問い合わせを受け付けました。担当者より2営業日以内にご連絡いたします。
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="mt-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  送信に失敗しました。お手数ですが、もう一度お試しください。
                </div>
              )}
            </form>

            {/* 直接連絡先 */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="text-center">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">お急ぎの方は直接お電話ください</h3>
                <div className="flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-8">
                  <div className="flex items-center">
                    <i className="ri-phone-line text-2xl text-blue-600 mr-3"></i>
                    <div>
                      <p className="text-lg font-semibold text-gray-800">03-1234-5678</p>
                      <p className="text-sm text-gray-600">営業時間：9:00〜18:00（土日祝除く）</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-mail-line text-2xl text-blue-600 mr-3"></i>
                    <div>
                      <p className="text-lg font-semibold text-gray-800">info@pm-realestate.co.jp</p>
                      <p className="text-sm text-gray-600">24時間受付</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactFormSection;
